import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { TbFileSearch } from "react-icons/tb";
import { Dimmer, Grid, Loader, Placeholder, Table, TableRow } from "semantic-ui-react";
import { useDeleteFacility, useGetFacilityPagesList } from "../../../api/facility";
import { useGetCountries } from "../../../api/static-data";
import PlusIcon from "../../../assets/commonIcon/PlusIcon";
import { CommonTable, CustomButton, InputText, TitleBar } from "../../../components";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import PaginationView from "../../../components/pagination/Pagination";
import { FACILITY_TYPES, FacilityTableCol } from "../../../config/constants";
import { successMessage } from "../../../helpers/ErrorHandler";
import FacilityDetailsModal from "../facilityDetails/FacilityDetailsModal";
import FacilityEditModal from "../facilityEdit/FacilityEditModal";
import FacilityRegisterModal from "../facilityRegister/FacilityRegisterModal";
import FacilityFilter from "./FacilityFilter";

const FacilityList = () => {
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [showDeleteFacilityModal, setShowDeleteFacilityModal] = useState(false);
  const [viewFacilityData, setViewFacilityData] = useState();
  const [editFacilityData, setEditFacilityData] = useState();
  const [deleteFacilityData, setDeleteFacilityData] = useState<any>({});
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [pageLimit, setPageLimit] = useState<any>(10);
  const [searchKey, setSearchKey] = useState<string>("");
  const [filteredFacilityTypes, setFilteredFacilityTypes] = useState<string[]>([]);

  const { register, watch } = useForm();
  const [searchFacility] = watch(["searchFacility"]);

  const {
    data: facilityList,
    isLoading: isFacilityListLoading,
    refetch
  } = useGetFacilityPagesList(pageNumber, pageLimit, searchFacility, filteredFacilityTypes);

  const { isLoading: editFacilityLoading, mutate: deleteFacility } = useDeleteFacility();

  const { data: countriesData, isLoading: isCountriesDataLoading } = useGetCountries();

  const refetchWithFilters = (newFacilityTypeIds: string[]) => {
    setFilteredFacilityTypes(newFacilityTypeIds);
  };

  const handlePagination = (currentPage: any) => {
    setPageNumber(currentPage);
  };

  const handlePageSize = (pageSize: any) => {
    setPageLimit(pageSize);
  };

  const getFacilityDetails = (selectedFacility: any) => {
    setViewFacilityData(selectedFacility);
    setEditFacilityData(selectedFacility);
    setVisibleDetailsModal(true);
  };

  const handleFacilityDelete = (detailsToDelete: any) => {
    setDeleteFacilityData(detailsToDelete);
    setShowDeleteFacilityModal(true);
  };

  const getFacilityForEditDetails = (selectedFacility: any) => {
    setEditFacilityData(selectedFacility);
    setVisibleEditModal(true);
  };

  const enableEditFacility = () => {
    setVisibleDetailsModal(false);
    setVisibleEditModal(true);
  };

  const enableFacilityView = () => {
    setVisibleDetailsModal(true);
    setVisibleEditModal(false);
  };

  //delete facility data
  const deleteFacilityDetails = (data: any) => {
    deleteFacility(data, {
      onSuccess: () => {
        setVisibleModal(false);
      }
    });
  };

  const loadTableData = () => {
    if (isFacilityListLoading || isCountriesDataLoading) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <Table.Cell colSpan={7}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </TableRow>
          ))}
        </>
      );
    }
    return facilityList?.result?.map((data: any, index: any) => {
      const formattedFacilityType = FACILITY_TYPES.find(
        type => type.value === data?.facilityType
      ) || { text: data?.facilityType, value: data?.facilityType };
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data?.facilityName}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{formattedFacilityType.text}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column computer={5} tablet={16} mobile={16} key={index}>
                <TbFileSearch
                  cursor={"pointer"}
                  onClick={() => {
                    getFacilityDetails({ ...data, facilityType: formattedFacilityType.value });
                  }}
                  size={24}
                  color="var(--tableEditIcon)"
                />
              </Grid.Column>
              <Grid.Column computer={5} tablet={16} mobile={16} key={index}>
                <MdModeEditOutline
                  cursor={"pointer"}
                  onClick={() => {
                    getFacilityForEditDetails(data);
                  }}
                  size={24}
                  color="var(--tableEditIcon)"
                />
              </Grid.Column>
              <Grid.Column computer={5} tablet={16} mobile={16} key={index}>
                <MdDeleteOutline
                  onClick={() => {
                    handleFacilityDelete({
                      _id: data?._id
                    });
                  }}
                  cursor={"pointer"}
                  size={24}
                  color="var(--tableEditIcon)"
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  // Loading state check
  if (editFacilityLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <TitleBar titleName={"Facilities"} />
      <Grid centered>
        <Grid.Column computer={4}>
          <InputText
            register={register}
            name="searchFacility"
            placeholder="Search Facility"
            defaultValues={searchKey}
            onChangeFunction={(e: any) => {
              setSearchKey(e.target.value);
            }}
          />
        </Grid.Column>
        <Grid.Column computer={2}>
          <FacilityFilter refetchWithFilters={refetchWithFilters} />
        </Grid.Column>
        <Grid.Column computer={10} textAlign="right">
          <CustomButton
            theme="green"
            title="New Facility"
            onClick={() => setVisibleModal(true)}
            iconWithText={true}
            iconChild={<PlusIcon />}
          />
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable
            enableTableFooter
            tableHeaderData={FacilityTableCol}
            tableFooterChild={
              <PaginationView
                changePageSize={handlePageSize}
                pageSize={pageLimit}
                totalPages={Math.ceil((facilityList?.total || 0) / pageLimit) || 1}
                currentPageNo={pageNumber}
                currentSelectPage={handlePagination}
              />
            }
          >
            {loadTableData()}
          </CommonTable>
        </Grid.Column>
      </Grid>
      <FacilityRegisterModal
        visibleModal={visibleModal}
        setVisibleModal={() => {
          setVisibleModal(false);
          refetch();
        }}
      />
      <FacilityEditModal
        visibleModal={visibleEditModal}
        setVisibleModal={() => {
          setVisibleEditModal(false);
          refetch();
        }}
        facilityData={editFacilityData}
        updateFacilityData={setEditFacilityData}
      />
      <FacilityDetailsModal
        visibleModal={visibleDetailsModal}
        setVisibleModal={() => setVisibleDetailsModal(false)}
        facilityData={viewFacilityData}
        countries={countriesData}
        setVisibleEdit={enableEditFacility}
      />
      <ConfirmModal
        viewModal={showDeleteFacilityModal}
        closeModal={() => setShowDeleteFacilityModal(false)}
        cancel={() => {
          setShowDeleteFacilityModal(false);
          setDeleteFacilityData({});
        }}
        approve={() => {
          deleteFacilityDetails(deleteFacilityData);
          successMessage("Facility removed successfully");
          setDeleteFacilityData({});
          setShowDeleteFacilityModal(false);
          refetch();
        }}
        title="Delete Facility Data"
        subTitle="Are you sure you want to delete the facility?"
      />
    </>
  );
};

export default FacilityList;
